import { useEffect, useMemo, useState } from "react";
import { internalApi, type SupportScriptRecord } from "../../lib/internal-api.js";
import type { ScriptPushLimits } from "../../lib/push-api.js";

// Форма «Пуш по папке (свои скрипты)» (запрос №10): тексты — из личных
// скриптов саппорта (раздел «Скрипты» в шаблонах) и/или вставленные пасты;
// при отправке идут по кругу. Пауза между отправками — случайная в
// диапазоне, нижняя граница не ниже серверного минимума. Последний набор
// запоминается в браузере по аккаунту, чтобы повторный запуск был в один клик.

export interface ScriptPushValue {
  texts: string[];
  delayMinSec: number;
  delayMaxSec: number;
  maxChatsPerRun: number;
  error: string | null;
}

interface ScriptPushFormProps {
  accountId: string;
  wsUrl: string;
  limits: ScriptPushLimits;
  defaultMaxChats: number;
  onChange(value: ScriptPushValue): void;
}

interface StoredDraft {
  scriptIds: string[];
  pastes: string[];
  delayMin: string;
  delayMax: string;
  maxChats: string;
}

// Ниже этой паузы — предупреждение (но не запрет): частые одинаковые
// сообщения разным людям — главный триггер спам-ограничений Telegram.
const RISKY_DELAY_SEC = 20;

const draftKey = (accountId: string): string => `tg-client:script-push:${accountId}`;

const readDraft = (accountId: string): Partial<StoredDraft> => {
  try {
    const raw = window.localStorage.getItem(draftKey(accountId));
    return raw ? (JSON.parse(raw) as Partial<StoredDraft>) : {};
  } catch {
    return {};
  }
};

const writeDraft = (accountId: string, draft: StoredDraft): void => {
  try {
    window.localStorage.setItem(draftKey(accountId), JSON.stringify(draft));
  } catch {
    // приватный режим / квота — просто не запоминаем
  }
};

const scriptTitleFromText = (text: string): string => {
  const line = text.trim().split("\n")[0] ?? "";
  return line.length > 40 ? `${line.slice(0, 40)}…` : line || "Паста для пуша";
};

export const ScriptPushForm = ({ accountId, wsUrl, limits, defaultMaxChats, onChange }: ScriptPushFormProps) => {
  const stored = useMemo(() => readDraft(accountId), [accountId]);
  const [saved, setSaved] = useState<SupportScriptRecord[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    Array.isArray(stored.scriptIds) ? stored.scriptIds : []
  );
  const [pastes, setPastes] = useState<string[]>(
    Array.isArray(stored.pastes) && stored.pastes.length > 0 ? stored.pastes : [""]
  );
  const [delayMin, setDelayMin] = useState(stored.delayMin ?? String(limits.defaultDelayMinSec));
  const [delayMax, setDelayMax] = useState(stored.delayMax ?? String(limits.defaultDelayMaxSec));
  const [maxChats, setMaxChats] = useState(stored.maxChats ?? String(defaultMaxChats));
  const [savingIndex, setSavingIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    internalApi
      .listSupportScripts(wsUrl)
      .then(({ scripts }) => {
        if (cancelled) return;
        setSaved(scripts);
        // Скрипт удалили — снимаем его выбор, иначе счётчик врал бы.
        setSelectedIds((current) => current.filter((id) => scripts.some((entry) => entry.scriptId === id)));
      })
      .catch((cause: unknown) => {
        if (!cancelled) setLoadError(cause instanceof Error ? cause.message : "Не удалось загрузить скрипты");
      });
    return () => {
      cancelled = true;
    };
  }, [wsUrl]);

  const texts = useMemo(() => {
    const fromSaved = (saved ?? [])
      .filter((entry) => selectedIds.includes(entry.scriptId))
      .map((entry) => entry.content.trim());
    const fromPastes = pastes.map((entry) => entry.trim());
    return [...fromSaved, ...fromPastes].filter((entry) => entry.length > 0);
  }, [pastes, saved, selectedIds]);

  const minNum = Number(delayMin);
  const maxNum = Number(delayMax);
  const chatsNum = Number(maxChats);
  const error = useMemo((): string | null => {
    if (texts.length === 0) return "Выберите скрипт или вставьте хотя бы один текст.";
    if (texts.length > limits.maxScripts) return `Не больше ${limits.maxScripts} текстов в одном пуше.`;
    const longIndex = texts.findIndex((entry) => entry.length > limits.maxScriptChars);
    if (longIndex >= 0) return `Текст №${longIndex + 1} длиннее ${limits.maxScriptChars} символов.`;
    if (!Number.isFinite(minNum) || !Number.isFinite(maxNum)) return "Укажите паузу числами.";
    if (minNum < limits.minDelaySec) return `Пауза не меньше ${limits.minDelaySec} с — иначе Telegram ограничит отправку.`;
    if (maxNum > limits.maxDelaySec) return `Пауза не больше ${limits.maxDelaySec} с.`;
    if (maxNum < minNum) return "«До» должно быть не меньше «от».";
    if (!Number.isFinite(chatsNum) || chatsNum < 1 || chatsNum > 1000) return "Максимум чатов — от 1 до 1000.";
    return null;
  }, [chatsNum, limits, maxNum, minNum, texts]);

  useEffect(() => {
    onChange({
      texts,
      delayMinSec: Math.floor(minNum),
      delayMaxSec: Math.floor(maxNum),
      maxChatsPerRun: Math.floor(chatsNum),
      error
    });
  }, [chatsNum, error, maxNum, minNum, onChange, texts]);

  useEffect(() => {
    writeDraft(accountId, { scriptIds: selectedIds, pastes, delayMin, delayMax, maxChats });
  }, [accountId, delayMax, delayMin, maxChats, pastes, selectedIds]);

  const toggleSaved = (scriptId: string, checked: boolean): void => {
    setSelectedIds((current) =>
      checked ? [...current.filter((id) => id !== scriptId), scriptId] : current.filter((id) => id !== scriptId)
    );
  };

  const setPaste = (index: number, value: string): void => {
    setPastes((current) => current.map((entry, position) => (position === index ? value : entry)));
  };

  const removePaste = (index: number): void => {
    setPastes((current) => {
      const next = current.filter((_, position) => position !== index);
      return next.length > 0 ? next : [""];
    });
  };

  // Паста → личный скрипт: пригодится и в следующих пушах, и в чате.
  const savePaste = async (index: number): Promise<void> => {
    const content = pastes[index]?.trim() ?? "";
    if (!content) return;
    setSavingIndex(index);
    setLoadError(null);
    try {
      const { script } = await internalApi.createSupportScript(
        { title: scriptTitleFromText(content), content },
        wsUrl
      );
      setSaved((current) => [...(current ?? []), script]);
      setSelectedIds((current) => [...current, script.scriptId]);
      removePaste(index);
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : "Не удалось сохранить скрипт");
    } finally {
      setSavingIndex(null);
    }
  };

  return (
    <div className="script-push">
      <div className="push-items__title">
        Мои скрипты
        <small>Создаются в панели шаблонов → «Скрипты»; отмеченные пойдут в ротацию</small>
      </div>
      {loadError ? <div className="side-card__error">{loadError}</div> : null}
      {saved === null ? (
        <div className="folder-autopick__note">Загружаем скрипты…</div>
      ) : saved.length === 0 ? (
        <div className="settings-card__hint">Сохранённых скриптов пока нет — вставьте тексты ниже.</div>
      ) : (
        <ul className="script-push__saved">
          {saved.map((entry) => (
            <li key={entry.scriptId}>
              <label className="script-push__saved-item">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(entry.scriptId)}
                  onChange={(event) => toggleSaved(entry.scriptId, event.target.checked)}
                />
                <span className="script-push__saved-text">
                  <strong>{entry.title}</strong>
                  <span>{entry.content}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <div className="push-items__title">
        Пасты
        <small>Тексты только для этого пуша; «В мои скрипты» — сохранить на будущее</small>
      </div>
      <ul className="script-push__pastes">
        {pastes.map((entry, index) => (
          <li key={index} className="script-push__paste">
            <textarea
              value={entry}
              rows={3}
              maxLength={limits.maxScriptChars}
              placeholder={`Текст №${index + 1}`}
              onChange={(event) => setPaste(index, event.target.value)}
            />
            <div className="script-push__paste-actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={() => void savePaste(index)}
                disabled={entry.trim().length === 0 || savingIndex !== null}
              >
                {savingIndex === index ? "Сохраняем…" : "В мои скрипты"}
              </button>
              <button
                type="button"
                className="button button--ghost chat-selection-bar__danger"
                onClick={() => removePaste(index)}
                disabled={pastes.length === 1 && entry.length === 0}
              >
                Убрать
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="button button--ghost script-push__add"
        onClick={() => setPastes((current) => [...current, ""])}
        disabled={texts.length >= limits.maxScripts}
      >
        + Добавить пасту
      </button>

      <div className="script-push__params">
        <label>
          Пауза от, с
          <input
            type="number"
            min={limits.minDelaySec}
            max={limits.maxDelaySec}
            value={delayMin}
            onChange={(event) => setDelayMin(event.target.value)}
          />
        </label>
        <label>
          до, с
          <input
            type="number"
            min={limits.minDelaySec}
            max={limits.maxDelaySec}
            value={delayMax}
            onChange={(event) => setDelayMax(event.target.value)}
          />
        </label>
        <label>
          Максимум чатов
          <input type="number" min={1} max={1000} value={maxChats} onChange={(event) => setMaxChats(event.target.value)} />
        </label>
      </div>
      <div className="folder-autopick__note">
        В ротации: {texts.length} {texts.length === 1 ? "текст" : "текстов"} — чаты получают их по кругу.
        Пауза между отправками — случайная в заданном диапазоне; параллельные папки одного аккаунта
        делят общий темп, поэтому частота сообщений от аккаунта не растёт.
        {Number.isFinite(minNum) && minNum >= limits.minDelaySec && minNum < RISKY_DELAY_SEC
          ? ` Пауза меньше ${RISKY_DELAY_SEC} с повышает риск ограничения Telegram.`
          : ""}
      </div>
      {error ? <div className="side-card__error">{error}</div> : null}
    </div>
  );
};
